"use client";

import type { CSSProperties } from "react";
import { Skeleton } from "@/components/design-system/ArtistDesignSystem";

const heroStyle: CSSProperties = {
	position: "relative",
	minHeight: "calc(100vw * 0.75)",
	overflow: "hidden",
	background: "linear-gradient(180deg, rgb(41 46 56 / 1), rgb(20 23 26 / 1))",
};

const heroContentStyle: CSSProperties = {
	position: "relative",
	zIndex: 1,
	display: "grid",
	gap: 8,
	padding: "calc(env(safe-area-inset-top, 0px) + 18px) 24px 32px",
};

const tabsStyle: CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(4, 1fr)",
	padding: "12px 0",
	borderBottom: "1px solid var(--ds-divider)",
};

const gridStyle: CSSProperties = {
	display: "grid",
	gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
	gap: 1,
};

const TAB_SKELETON_IDS: readonly string[] = Array.from(
	{ length: 4 },
	(_, index) => `tab-skeleton-${index}`,
);

const GRID_SKELETON_IDS: readonly string[] = Array.from(
	{ length: 9 },
	(_, index) => `grid-skeleton-${index}`,
);

/**
 * LibrarySkeleton — full-page loading state matching `ProfileHeaderSkeleton`
 * + `LibraryTabsSkeleton` + `WorksGridSkeleton` on iOS. Hero gradient with
 * metric + profile shimmer lines, 4-tab shimmer row, 3×3 square grid.
 */
export function LibrarySkeleton() {
	return (
		<div className="library-skeleton">
			<section className="library-skeleton__hero" style={heroStyle}>
				<div style={heroContentStyle}>
					<MetricPanelSkeleton />
					<ProfileSectionSkeleton />
				</div>
			</section>
			<div className="library-skeleton__tabs" style={tabsStyle}>
				{TAB_SKELETON_IDS.map((tabId) => (
					<div
						key={tabId}
						style={{
							display: "grid",
							gap: 8,
							padding: "0 12px",
							justifyItems: "center",
						}}
					>
						<Skeleton shape="rectangle" width={48} height={16} />
						<div style={{ height: 2 }} />
					</div>
				))}
			</div>
			<div className="library-skeleton__grid" style={gridStyle}>
				{GRID_SKELETON_IDS.map((gridId) => (
					<Skeleton
						key={gridId}
						shape="rectangle"
						width="100%"
						height={0}
						className="library-skeleton__grid-tile"
					/>
				))}
			</div>
		</div>
	);
}

function MetricPanelSkeleton() {
	return (
		<div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
			<Skeleton shape="rectangle" width={100} height={17} />
			<Skeleton shape="rectangle" width={80} height={34} />
			<Skeleton shape="rectangle" width={140} height={13} />
		</div>
	);
}

function ProfileSectionSkeleton() {
	return (
		<div
			style={{
				display: "grid",
				gap: 16,
				justifyItems: "center",
				width: "100%",
			}}
		>
			<Skeleton shape="circle" width={68} height={68} />
			<div style={{ display: "grid", gap: 6, justifyItems: "center" }}>
				<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
					<Skeleton shape="rectangle" width={100} height={24} />
					<Skeleton shape="capsule" width={60} height={24} />
				</div>
				<Skeleton shape="rectangle" width={120} height={14} />
			</div>
			<div
				style={{
					display: "flex",
					gap: 32,
					justifyContent: "center",
					width: "100%",
				}}
			>
				<StatSkeleton />
				<StatSkeleton />
				<StatSkeleton />
			</div>
			<Skeleton shape="rectangle" width={200} height={13} />
		</div>
	);
}

function StatSkeleton() {
	return (
		<div
			style={{ display: "grid", gap: 4, justifyItems: "center", minWidth: 64 }}
		>
			<Skeleton shape="rectangle" width={40} height={17} />
			<Skeleton shape="rectangle" width={30} height={12} />
		</div>
	);
}
